import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Upload, Loader2, Plus, Trash2, Mic, Type, Play, X, Star, RotateCw, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import InterviewUpgradeGate from "@/components/InterviewUpgradeGate";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result;
      const b64 = typeof res === "string" ? res.split(",")[1] : "";
      resolve(b64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function InterviewBank() {
  const { id } = useParams();
  const { t } = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef(null);

  const [bank, setBank] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ pct: 0, completed: 0, total: 0 });
  const [adding, setAdding] = useState(false);

  // manual add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [qText, setQText] = useState("");
  const [qAnswers, setQAnswers] = useState("");
  const [qMinReq, setQMinReq] = useState(1);

  // start session
  const [mode, setMode] = useState("text");
  const [order, setOrder] = useState("random");
  const [limit, setLimit] = useState(""); // empty string = "all"
  const [showQuestions, setShowQuestions] = useState(true); // ear training option
  const [onlyStarred, setOnlyStarred] = useState(false);
  const [speed, setSpeed] = useState(1.0); // TTS speech speed
  const [voice, setVoice] = useState("nova"); // TTS voice (female=nova, male=onyx, etc.)
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [starring, setStarring] = useState(null);
  const [starting, setStarting] = useState(false);
  const [activeSession, setActiveSession] = useState(null);

  const loadActiveSession = async () => {
    try {
      const { data } = await api.get(`/interview/banks/${id}/active-session`);
      setActiveSession(data?.session || null);
    } catch {
      // soft-fail — resume card just won't show
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/interview/banks/${id}`);
      setBank(data.bank);
      setQuestions(data.questions || []);
      loadActiveSession();
    } catch (e) {
      toast.error(t("iv_load_fail"));
      navigate("/app/interview");
    } finally {
      setLoading(false);
    }
  };

  const locked = user && !user.is_admin && (user.plan || "free") === "free";
  useEffect(() => { if (!locked) load(); else setLoading(false); }, [id, locked]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length > 100) {
      toast.error(t("iv_max_100"));
      return;
    }
    // Guard against ingress timeouts: warn if total payload would exceed ~25MB
    const totalBytes = files.reduce((acc, f) => acc + (f.size || 0), 0);
    if (totalBytes > 25 * 1024 * 1024) {
      const sizeMb = Math.round(totalBytes / (1024 * 1024));
      const ok = window.confirm(t("iv_large_upload_warn", { mb: sizeMb }));
      if (!ok) return;
    }
    setUploading(true);
    setUploadProgress({ pct: 0, completed: 0, total: 0 });
    try {
      const images = [];
      const pdfs = [];
      for (const f of files) {
        const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name);
        const b64 = await fileToBase64(f);
        if (isPdf) pdfs.push(b64); else images.push(b64);
      }
      // Kick off async extraction job (with one auto-retry on transient 502/504)
      let jobResp;
      try {
        const r1 = await api.post(`/interview/banks/${id}/extract/async`, {
          images_base64: images, pdfs_base64: pdfs,
        });
        jobResp = r1.data;
      } catch (e) {
        const code = e?.response?.status;
        if (code === 502 || code === 503 || code === 504 || !e.response) {
          // Wait a moment then retry once
          await new Promise((r) => setTimeout(r, 1500));
          const r2 = await api.post(`/interview/banks/${id}/extract/async`, {
            images_base64: images, pdfs_base64: pdfs,
          });
          jobResp = r2.data;
        } else {
          throw e;
        }
      }
      const jobId = jobResp.job_id;
      // Poll until done or error
      let last = null;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 1500));
        let job;
        try {
          const { data } = await api.get(`/interview/extract/jobs/${jobId}`);
          job = data;
        } catch (err) {
          throw new Error("Lost connection to extraction job");
        }
        setUploadProgress({
          pct: job.progress || 0,
          completed: job.completed_batches || 0,
          total: job.total_batches || 0,
        });
        last = job;
        if (job.status === "done" || job.status === "error") break;
      }
      if (last && last.status === "error") {
        const err = last.error || t("iv_extract_fail");
        if (last.http_status === 402) {
          toast.error(err, { duration: 10000 });
        } else {
          toast.error(err);
        }
      } else if (last) {
        if (last.budget_exhausted) {
          toast.warning(t("iv_budget_partial", { n: last.added }), { duration: 8000 });
        } else if ((last.batches_failed || 0) > 0) {
          toast.warning(t("iv_partial_extract", { n: last.added, f: last.batches_failed }));
        } else {
          toast.success(`${last.added} ${t("iv_extracted")}`);
        }
        await load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || t("iv_extract_fail"));
    } finally {
      setUploading(false);
      setUploadProgress({ pct: 0, completed: 0, total: 0 });
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleManualAdd = async (e) => {
    e.preventDefault();
    const answers = qAnswers.split("\n").map((a) => a.trim()).filter(Boolean);
    if (!qText.trim() || answers.length === 0) {
      toast.error(t("iv_need_q_and_a"));
      return;
    }
    setAdding(true);
    try {
      await api.post(`/interview/banks/${id}/questions`, {
        question: qText.trim(),
        accepted_answers: answers,
        min_required: Math.max(1, Math.min(parseInt(qMinReq, 10) || 1, answers.length)),
      });
      setQText(""); setQAnswers(""); setQMinReq(1); setShowAddForm(false);
      toast.success(t("iv_q_added"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("iv_add_fail"));
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteQ = async (qid) => {
    const deletedQ = questions.find((q) => q.id === qid);
    try {
      await api.delete(`/interview/questions/${qid}`);
      setQuestions((qs) => qs.filter((q) => q.id !== qid));
      toast.success(t("iv_q_deleted_undo"), {
        duration: 8000,
        action: {
          label: t("iv_undo"),
          onClick: async () => {
            try {
              await api.post(`/interview/questions/${qid}/restore`);
              await load();
              toast.success(t("iv_restored"));
            } catch {
              toast.error(t("iv_restore_fail"));
            }
          },
        },
      });
    } catch (e) {
      toast.error(t("iv_delete_fail"));
    }
  };

  const handleToggleStar = async (qid) => {
    setStarring(qid);
    try {
      const { data } = await api.patch(`/interview/questions/${qid}/star`);
      setQuestions((qs) => qs.map((q) => (q.id === qid ? { ...q, is_starred: data.is_starred } : q)));
    } catch (e) {
      toast.error(t("iv_delete_fail"));
    } finally {
      setStarring(null);
    }
  };

  // Toggle "ask in sessions" flag — keeps question in the bank but
  // excludes it from all future practice sessions. Optimistic UI so
  // the user sees the state flip instantly; rollback on failure.
  const [togglingEnabled, setTogglingEnabled] = useState(null);
  const handleToggleEnabled = async (qid, currentlyEnabled) => {
    const nextEnabled = !currentlyEnabled;
    setTogglingEnabled(qid);
    setQuestions((qs) => qs.map((q) => (q.id === qid ? { ...q, enabled: nextEnabled } : q)));
    try {
      await api.patch(`/interview/questions/${qid}/enabled`, { enabled: nextEnabled });
    } catch (e) {
      setQuestions((qs) => qs.map((q) => (q.id === qid ? { ...q, enabled: currentlyEnabled } : q)));
      toast.error(t("iv_delete_fail"));
    } finally {
      setTogglingEnabled(null);
    }
  };

  // Inline manual edit — user hits the pencil, the row expands into an
  // editable form pre-filled with the current question + answers. Save
  // sends a PATCH; Cancel discards. Optimistic-free: we only mutate
  // local state after the server confirms so we never show a stale row
  // if the request fails (validation, network, etc.).
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ question: "", answers: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const beginEdit = (q) => {
    setEditingId(q.id);
    setEditDraft({
      question: q.question || "",
      // Show answers one per line so the user can freely add/remove them
      // without wrestling with commas inside answer text.
      answers: (q.accepted_answers || []).join("\n"),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ question: "", answers: "" });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const question = editDraft.question.trim();
    const answers = editDraft.answers
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!question) {
      toast.error(t("iv_edit_question_empty", "Question cannot be empty"));
      return;
    }
    if (answers.length === 0) {
      toast.error(t("iv_edit_answers_empty", "At least one accepted answer is required"));
      return;
    }
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/interview/questions/${editingId}`, {
        question,
        accepted_answers: answers,
      });
      setQuestions((qs) => qs.map((q) => (q.id === editingId ? { ...q, ...data } : q)));
      toast.success(t("iv_edit_saved", "Question updated"));
      cancelEdit();
    } catch (e) {
      const msg = e?.response?.data?.detail || t("iv_edit_fail", "Could not save changes");
      toast.error(typeof msg === "string" ? msg : t("iv_edit_fail", "Could not save changes"));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleStart = async () => {
    if (questions.length === 0) {
      toast.error(t("iv_no_questions_start"));
      return;
    }
    setStarting(true);
    try {
      const payload = { bank_id: id, mode, order, only_starred: onlyStarred };
      if (limit !== "" && limit !== "all") {
        let n = parseInt(limit, 10);
        if (!Number.isFinite(n) || n < 1) n = 1;
        const pool = onlyStarred ? questions.filter((q) => q.is_starred).length : questions.length;
        if (n > pool) {
          toast.message(t("iv_limit_clamped", { n: pool }));
          n = pool;
        }
        payload.limit = n;
      }
      // Range filter (1-based, source-order)
      const rs = parseInt(rangeStart, 10);
      const re = parseInt(rangeEnd, 10);
      if (Number.isFinite(rs) && rs >= 1) payload.start_index = rs;
      if (Number.isFinite(re) && re >= 1) payload.end_index = re;
      if (payload.start_index && payload.end_index && payload.end_index < payload.start_index) {
        toast.error(t("iv_range_invalid"));
        setStarting(false);
        return;
      }
      const { data } = await api.post("/interview/sessions/start", payload);
      const params = new URLSearchParams();
      if (!showQuestions) params.set("hide", "1");
      if (speed !== 1.0) params.set("speed", String(speed));
      if (voice !== "nova") params.set("voice", voice);
      const qs = params.toString();
      navigate(`/app/interview/session/${data.session_id}${qs ? "?" + qs : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("iv_start_fail"));
    } finally {
      setStarting(false);
    }
  };

  const setLimitFromChip = (val) => setLimit(val === "all" ? "" : String(val));
  const onLimitInputChange = (e) => {
    const v = e.target.value;
    if (v === "") { setLimit(""); return; }
    const n = parseInt(v, 10);
    if (!Number.isFinite(n) || n < 1) { setLimit(""); return; }
    setLimit(String(Math.min(n, questions.length)));
  };

  if (user && !user.is_admin && (user.plan || "free") === "free") {
    return <InterviewUpgradeGate />;
  }
  if (loading) return <div className="p-8 text-zinc-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {t("iv_loading")}</div>;
  if (!bank) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="interview-bank-page">
      <Link to="/app/interview" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> {t("iv_back_library")}
      </Link>

      <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">{bank.name}</h1>
          {bank.description && <p className="mt-2 text-zinc-600">{bank.description}</p>}
          <div className="mt-2 text-sm text-zinc-500">{questions.length} {t("iv_questions")}</div>
        </div>
      </div>

      {/* Resume in-progress session */}
      {activeSession && (
        <div className="mt-8 border-2 border-orange-400 bg-orange-50 p-5 rounded-md flex items-start gap-4 flex-wrap" data-testid="resume-session-card">
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-700">{t("iv_resume_eyebrow")}</div>
            <div className="mt-1 text-lg font-black">{t("iv_resume_title")}</div>
            <div className="mt-1 text-sm text-zinc-700">
              {t("iv_resume_progress", { done: activeSession.current_index || 0, total: activeSession.total || 0 })}
              {" · "}
              <span className="text-emerald-700 font-bold">{activeSession.correct || 0} ✓</span>
              {" / "}
              <span className="text-red-700 font-bold">{activeSession.wrong || 0} ✗</span>
              {" · "}
              <span className="capitalize">{activeSession.mode === "voice" ? t("iv_mode_voice") : t("iv_mode_text")}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const params = new URLSearchParams();
                if (!showQuestions) params.set("hide", "1");
                if (speed !== 1.0) params.set("speed", String(speed));
                if (voice !== "nova") params.set("voice", voice);
                const qs = params.toString();
                navigate(`/app/interview/session/${activeSession.id}${qs ? "?" + qs : ""}`);
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white font-bold"
              data-testid="resume-session-btn"
            >
              <Play className="w-4 h-4" /> {t("iv_resume_btn")}
            </button>
            <button
              onClick={async () => {
                if (!window.confirm(t("iv_resume_discard_confirm"))) return;
                try {
                  await api.post(`/interview/sessions/${activeSession.id}/finish`);
                  setActiveSession(null);
                  toast.success(t("iv_resume_discarded"));
                } catch {
                  toast.error(t("iv_skip_fail"));
                }
              }}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-zinc-300 hover:bg-white rounded-md text-zinc-700"
              data-testid="resume-discard-btn"
              title={t("iv_resume_discard")}
            >
              <X className="w-4 h-4" /> {t("iv_resume_discard")}
            </button>
          </div>
        </div>
      )}

      {/* Start Session */}
      <div className="mt-8 border border-zinc-900 bg-zinc-900 text-white p-6 rounded-md" data-testid="interview-start-block">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{t("iv_practice")}</div>
        <h2 className="mt-1 text-2xl font-black">{t("iv_start_h2")}</h2>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            onClick={() => setMode("text")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold border ${mode === "text" ? "bg-white text-zinc-900 border-white" : "bg-transparent text-white border-zinc-700 hover:border-white"}`}
            data-testid="mode-text-btn"
          >
            <Type className="w-4 h-4" /> {t("iv_mode_text")}
          </button>
          <button
            onClick={() => setMode("voice")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold border ${mode === "voice" ? "bg-white text-zinc-900 border-white" : "bg-transparent text-white border-zinc-700 hover:border-white"}`}
            data-testid="mode-voice-btn"
          >
            <Mic className="w-4 h-4" /> {t("iv_mode_voice")}
          </button>
          <button
            onClick={handleStart}
            disabled={starting || questions.length === 0}
            className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white font-bold disabled:opacity-50"
            data-testid="start-session-btn"
          >
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {t("iv_start_now")}
          </button>
        </div>

        {/* Order + Question count */}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-2">{t("iv_order_label")}</div>
            <div className="flex gap-2">
              <button
                onClick={() => setOrder("random")}
                className={`px-3 py-1.5 text-sm rounded-md border ${order === "random" ? "bg-white text-zinc-900 border-white" : "bg-transparent text-white border-zinc-700 hover:border-white"}`}
                data-testid="order-random-btn"
              >
                🎲 {t("iv_order_random")}
              </button>
              <button
                onClick={() => setOrder("sequential")}
                className={`px-3 py-1.5 text-sm rounded-md border ${order === "sequential" ? "bg-white text-zinc-900 border-white" : "bg-transparent text-white border-zinc-700 hover:border-white"}`}
                data-testid="order-sequential-btn"
              >
                ↓ {t("iv_order_sequential")}
              </button>
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-2">
              {t("iv_limit_label")} <span className="text-zinc-500 normal-case tracking-normal">({t("iv_limit_max", { n: questions.length })})</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="number"
                min={1}
                max={questions.length}
                value={limit}
                onChange={onLimitInputChange}
                placeholder={String(questions.length)}
                className="w-24 px-3 py-1.5 text-sm bg-zinc-800 text-white border border-zinc-700 rounded-md focus:outline-none focus:border-white"
                data-testid="limit-input"
              />
              <span className="text-xs text-zinc-500">{t("iv_or")}</span>
              <button
                type="button"
                onClick={() => setLimitFromChip("all")}
                className={`px-2.5 py-1 text-xs rounded-md border ${limit === "" ? "bg-white text-zinc-900 border-white" : "bg-transparent text-white border-zinc-700 hover:border-white"}`}
                data-testid="limit-chip-all"
              >
                {t("iv_limit_all_short")}
              </button>
              {[5, 10, 20, 50].filter((n) => n < questions.length).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setLimitFromChip(n)}
                  className={`px-2.5 py-1 text-xs rounded-md border ${String(n) === limit ? "bg-white text-zinc-900 border-white" : "bg-transparent text-white border-zinc-700 hover:border-white"}`}
                  data-testid={`limit-chip-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Range filter (from question X to Y) */}
        <div className="mt-5">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-2">
            {t("iv_range_label")} <span className="text-zinc-500 normal-case tracking-normal">({t("iv_range_hint", { n: questions.length })})</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-zinc-400">{t("iv_range_from")}</span>
            <input
              type="number"
              min={1}
              max={questions.length}
              value={rangeStart}
              onChange={(e) => setRangeStart(e.target.value)}
              placeholder="1"
              className="w-20 px-3 py-1.5 text-sm bg-zinc-800 text-white border border-zinc-700 rounded-md focus:outline-none focus:border-white"
              data-testid="range-start-input"
            />
            <span className="text-xs text-zinc-400">{t("iv_range_to")}</span>
            <input
              type="number"
              min={1}
              max={questions.length}
              value={rangeEnd}
              onChange={(e) => setRangeEnd(e.target.value)}
              placeholder={String(questions.length)}
              className="w-20 px-3 py-1.5 text-sm bg-zinc-800 text-white border border-zinc-700 rounded-md focus:outline-none focus:border-white"
              data-testid="range-end-input"
            />
            {(rangeStart || rangeEnd) && (
              <button
                onClick={() => { setRangeStart(""); setRangeEnd(""); }}
                className="text-xs text-zinc-400 hover:text-white underline ml-1"
                data-testid="range-clear-btn"
              >
                {t("iv_range_clear")}
              </button>
            )}
          </div>
          <div className="mt-1 text-xs text-zinc-400">{t("iv_range_note")}</div>
        </div>

        {/* Speech speed (TTS) */}
        <div className="mt-5">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-2">{t("iv_speed_label")}</div>
          <div className="flex flex-wrap gap-2">
            {[
              { v: 0.75, label: t("iv_speed_slow") },
              { v: 1.0, label: t("iv_speed_normal") },
              { v: 1.25, label: t("iv_speed_fast") },
              { v: 1.5, label: t("iv_speed_faster") },
            ].map(({ v, label }) => (
              <button
                key={v}
                onClick={() => setSpeed(v)}
                className={`px-3 py-1.5 text-sm rounded-md border ${speed === v ? "bg-white text-zinc-900 border-white" : "bg-transparent text-white border-zinc-700 hover:border-white"}`}
                data-testid={`speed-${v}-btn`}
              >
                {label} <span className="text-[10px] opacity-70 font-mono">({v}x)</span>
              </button>
            ))}
          </div>
          <div className="mt-1 text-xs text-zinc-400">{t("iv_speed_hint")}</div>
        </div>

        {/* Voice gender */}
        <div className="mt-5">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-2">{t("iv_voice_label")}</div>
          <div className="flex flex-wrap gap-2">
            {[
              { v: "nova", label: t("iv_voice_female"), emoji: "👩" },
              { v: "ash", label: t("iv_voice_male"), emoji: "👨" },
              { v: "shimmer", label: t("iv_voice_female2"), emoji: "👩‍🦱" },
              { v: "fable", label: t("iv_voice_male2"), emoji: "🧔" },
            ].map(({ v, label, emoji }) => (
              <button
                key={v}
                onClick={() => setVoice(v)}
                className={`px-3 py-1.5 text-sm rounded-md border ${voice === v ? "bg-white text-zinc-900 border-white" : "bg-transparent text-white border-zinc-700 hover:border-white"}`}
                data-testid={`voice-${v}-btn`}
              >
                {emoji} {label}
              </button>
            ))}
          </div>
          <div className="mt-1 text-xs text-zinc-400">{t("iv_voice_hint")}</div>
        </div>

        {/* Show / Hide questions option */}
        <div className="mt-5">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-2">{t("iv_show_q_label")}</div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowQuestions(true)}
              className={`px-3 py-1.5 text-sm rounded-md border ${showQuestions ? "bg-white text-zinc-900 border-white" : "bg-transparent text-white border-zinc-700 hover:border-white"}`}
              data-testid="show-q-on-btn"
            >
              👁 {t("iv_show_q_on")}
            </button>
            <button
              onClick={() => setShowQuestions(false)}
              className={`px-3 py-1.5 text-sm rounded-md border ${!showQuestions ? "bg-white text-zinc-900 border-white" : "bg-transparent text-white border-zinc-700 hover:border-white"}`}
              data-testid="show-q-off-btn"
            >
              🔊 {t("iv_show_q_off")}
            </button>
          </div>
          <div className="mt-1 text-xs text-zinc-400">
            {showQuestions ? t("iv_show_q_on_hint") : t("iv_show_q_off_hint")}
          </div>
        </div>

        {/* Only starred questions option */}
        {questions.some((q) => q.is_starred) && (
          <div className="mt-5">
            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-2">{t("iv_starred_label")}</div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setOnlyStarred(false)}
                className={`px-3 py-1.5 text-sm rounded-md border ${!onlyStarred ? "bg-white text-zinc-900 border-white" : "bg-transparent text-white border-zinc-700 hover:border-white"}`}
                data-testid="all-q-btn"
              >
                {t("iv_starred_all")} ({questions.length})
              </button>
              <button
                onClick={() => setOnlyStarred(true)}
                className={`px-3 py-1.5 text-sm rounded-md border ${onlyStarred ? "bg-amber-300 text-zinc-900 border-amber-300" : "bg-transparent text-white border-zinc-700 hover:border-white"}`}
                data-testid="only-starred-btn"
              >
                ⭐ {t("iv_starred_only")} ({questions.filter((q) => q.is_starred).length})
              </button>
            </div>
            <div className="mt-1 text-xs text-zinc-400">{t("iv_starred_hint")}</div>
          </div>
        )}

        <div className="mt-3 text-xs text-zinc-400">
          {mode === "voice" ? t("iv_mode_voice_hint") : t("iv_mode_text_hint")}
        </div>
      </div>

      {/* Add Questions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-zinc-200 bg-white p-6 rounded-md">
          <div className="font-bold flex items-center gap-2"><Upload className="w-4 h-4" /> {t("iv_upload_title")}</div>
          <p className="mt-2 text-sm text-zinc-600">{t("iv_upload_sub")}</p>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*,application/pdf,.pdf"
            onChange={handleUpload}
            disabled={uploading}
            className="hidden"
            data-testid="upload-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 hover:bg-zinc-50 rounded-md font-semibold disabled:opacity-60"
            data-testid="upload-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? t("iv_extracting") : t("iv_choose_files")}
          </button>
          {uploading && (
            <div className="mt-3" data-testid="upload-progress">
              <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-orange-500 transition-all"
                  style={{ width: `${uploadProgress.pct}%` }}
                />
              </div>
              <div className="mt-1 text-xs text-zinc-500 flex justify-between">
                <span>{uploadProgress.total > 0 ? t("iv_progress_label", { c: uploadProgress.completed, n: uploadProgress.total }) : t("iv_progress_preparing")}</span>
                <span>{uploadProgress.pct}%</span>
              </div>
            </div>
          )}
        </div>

        <div className="border border-zinc-200 bg-white p-6 rounded-md">
          <div className="font-bold flex items-center gap-2"><Plus className="w-4 h-4" /> {t("iv_manual_title")}</div>
          <p className="mt-2 text-sm text-zinc-600">{t("iv_manual_sub")}</p>
          <button
            onClick={() => setShowAddForm((s) => !s)}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 hover:bg-zinc-50 rounded-md font-semibold"
            data-testid="manual-add-toggle"
          >
            <Plus className="w-4 h-4" /> {showAddForm ? t("iv_cancel") : t("iv_add_question")}
          </button>
        </div>
      </div>

      {showAddForm && (
        <form onSubmit={handleManualAdd} className="mt-4 border border-zinc-900 bg-white p-6 rounded-md space-y-4" data-testid="manual-add-form">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("iv_question")}</label>
            <input
              required
              value={qText}
              onChange={(e) => setQText(e.target.value)}
              className="mt-1 w-full px-4 py-3 border border-zinc-200 rounded-md focus:outline-none focus:border-blue-600"
              data-testid="manual-q-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("iv_accepted_answers")}</label>
            <textarea
              required
              value={qAnswers}
              onChange={(e) => setQAnswers(e.target.value)}
              placeholder={t("iv_accepted_ph")}
              rows={4}
              className="mt-1 w-full px-4 py-3 border border-zinc-200 rounded-md focus:outline-none focus:border-blue-600 font-mono text-sm"
              data-testid="manual-a-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("iv_min_required")}</label>
            <input
              type="number" min={1} value={qMinReq}
              onChange={(e) => setQMinReq(e.target.value)}
              className="mt-1 w-24 px-4 py-3 border border-zinc-200 rounded-md focus:outline-none focus:border-blue-600"
              data-testid="manual-min-input"
            />
            <span className="ml-3 text-xs text-zinc-500">{t("iv_min_hint")}</span>
          </div>
          <button
            type="submit"
            disabled={adding}
            className="btn-accent inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-md disabled:opacity-60"
            data-testid="manual-submit"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {t("iv_add")}
          </button>
        </form>
      )}

      {/* Question list */}
      <div className="mt-8">
        <h2 className="text-lg font-bold">{t("iv_question_list")}</h2>
        {questions.length === 0 ? (
          <div className="mt-4 border border-dashed border-zinc-300 bg-white p-10 rounded-md text-center text-zinc-500">
            {t("iv_no_questions")}
          </div>
        ) : (
          <ul className="mt-4 space-y-2">
            {questions.map((q, i) => {
              const enabled = q.enabled !== false;   // legacy questions default to enabled
              return (
              <li
                key={q.id}
                className={`border p-4 rounded-md flex gap-3 transition-opacity ${
                  !enabled
                    ? "border-zinc-300 bg-zinc-50 opacity-60"
                    : q.is_starred
                    ? "border-amber-400 bg-amber-50/50"
                    : "border-zinc-200 bg-white"
                }`}
                data-testid={`q-row-${q.id}`}
              >
                <div className="text-xs font-mono text-zinc-400 shrink-0 w-8 pt-0.5">{i + 1}.</div>
                {editingId === q.id ? (
                  <div className="flex-1 min-w-0" data-testid={`q-edit-form-${q.id}`}>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1">
                      {t("iv_edit_question_label", "Question")}
                    </label>
                    <textarea
                      value={editDraft.question}
                      onChange={(e) => setEditDraft((d) => ({ ...d, question: e.target.value }))}
                      rows={2}
                      className="w-full text-sm border border-zinc-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zinc-900"
                      data-testid={`q-edit-question-${q.id}`}
                    />
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-500 mt-3 mb-1">
                      {t("iv_edit_answers_label", "Accepted answers (one per line)")}
                    </label>
                    <textarea
                      value={editDraft.answers}
                      onChange={(e) => setEditDraft((d) => ({ ...d, answers: e.target.value }))}
                      rows={Math.max(2, editDraft.answers.split("\n").length)}
                      className="w-full text-sm border border-zinc-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zinc-900 font-mono"
                      placeholder={t("iv_edit_answers_placeholder", "moon\nyerkuresi\nay")}
                      data-testid={`q-edit-answers-${q.id}`}
                    />
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={saveEdit}
                        disabled={savingEdit}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-zinc-900 hover:bg-black text-white text-xs font-bold disabled:opacity-50"
                        data-testid={`q-edit-save-${q.id}`}
                      >
                        {savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        {t("iv_edit_save", "Save")}
                      </button>
                      <button
                        onClick={cancelEdit}
                        disabled={savingEdit}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white hover:bg-zinc-50 border border-zinc-300 text-zinc-700 text-xs font-bold disabled:opacity-50"
                        data-testid={`q-edit-cancel-${q.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                        {t("iv_cancel", "Cancel")}
                      </button>
                    </div>
                  </div>
                ) : (
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2 flex-wrap">
                    {!enabled && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-zinc-700 text-white px-1.5 py-0.5 rounded font-bold"
                        data-testid={`q-skipped-badge-${q.id}`}
                      >
                        {t("iv_question_skipped_badge", "Skipped")}
                      </span>
                    )}
                    {q.is_starred && enabled && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-amber-400 text-zinc-900 px-1.5 py-0.5 rounded font-bold">⭐ {t("iv_starred_badge")}</span>}
                    <span className={!enabled ? "line-through decoration-zinc-400" : ""}>{q.question}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {q.accepted_answers.map((a, j) => (
                      <span key={j} className="inline-block text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded">
                        {a}
                      </span>
                    ))}
                  </div>
                  {q.min_required > 1 && (
                    <div className="mt-1 text-xs text-zinc-500">{t("iv_min_required")}: {q.min_required}</div>
                  )}
                </div>
                )}
                {editingId === q.id ? null : (
                <div className="flex items-start gap-1 shrink-0">
                  <button
                    onClick={() => beginEdit(q)}
                    className="shrink-0 p-1 rounded text-zinc-400 hover:text-zinc-900"
                    title={t("iv_edit_question_tooltip", "Edit question and answers")}
                    data-testid={`q-edit-${q.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleToggleEnabled(q.id, enabled)}
                    disabled={togglingEnabled === q.id}
                    className={`shrink-0 p-1 rounded text-xs font-bold ${
                      enabled
                        ? "text-emerald-600 hover:text-red-600"
                        : "text-zinc-400 hover:text-emerald-600"
                    }`}
                    title={enabled
                      ? t("iv_question_skip_tooltip", "Skip in future sessions (won't delete)")
                      : t("iv_question_unskip_tooltip", "Include in sessions again")}
                    data-testid={`q-toggle-enabled-${q.id}`}
                  >
                    {enabled ? "🚫" : "✓"}
                  </button>
                  <button
                    onClick={() => handleToggleStar(q.id)}
                    disabled={starring === q.id}
                    className={`shrink-0 p-1 rounded ${q.is_starred ? "text-amber-500 hover:text-amber-600" : "text-zinc-300 hover:text-amber-500"}`}
                    title={q.is_starred ? t("iv_unstar") : t("iv_star")}
                    data-testid={`q-star-${q.id}`}
                  >
                    <Star className={`w-4 h-4 ${q.is_starred ? "fill-amber-400" : ""}`} />
                  </button>
                  <button
                    onClick={() => handleDeleteQ(q.id)}
                    className="text-zinc-400 hover:text-red-600 shrink-0 p-1"
                    data-testid={`q-delete-${q.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                )}
              </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
